import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export const LAB_ORDER_STATUS = {
  TO_SEND: 'TO_SEND',
  SENT: 'SENT',
  RETURNED: 'RETURNED',
  IN_ADJUSTMENT: 'IN_ADJUSTMENT',
  DELIVERED: 'DELIVERED',
  CANCELED: 'CANCELED',
};

export const labOrderStatusConfig = {
  TO_SEND: { label: 'A enviar', color: 'secondary', variant: 'outlined' },
  SENT: { label: 'Enviado', color: 'info', variant: 'outlined' },
  RETURNED: { label: 'Retornado', color: 'primary', variant: 'filled' },
  IN_ADJUSTMENT: { label: 'Em ajuste', color: 'warning', variant: 'outlined' },
  DELIVERED: { label: 'Entregue', color: 'success', variant: 'filled' },
  CANCELED: { label: 'Cancelado', color: 'default', variant: 'outlined' },
};

// Which statuses can follow each one. Delivered and canceled are final.
export const labOrderStatusTransitions = {
  TO_SEND: [LAB_ORDER_STATUS.SENT, LAB_ORDER_STATUS.CANCELED],
  SENT: [LAB_ORDER_STATUS.RETURNED, LAB_ORDER_STATUS.CANCELED],
  IN_ADJUSTMENT: [LAB_ORDER_STATUS.RETURNED, LAB_ORDER_STATUS.CANCELED],
  RETURNED: [LAB_ORDER_STATUS.DELIVERED, LAB_ORDER_STATUS.IN_ADJUSTMENT],
  DELIVERED: [],
  CANCELED: [],
};

// The date asked when moving to each status. Its meaning depends on the target status:
// sent date, return date, new expected return date (adjustment) or delivery date to the patient.
// Registering the send also asks the expected return date.
export const labOrderStatusActions = {
  SENT: {
    menuLabel: 'Registrar envio',
    dialogTitle: 'Registrar envio ao laboratório',
    description: 'O trabalho saiu da clínica e está com o laboratório.',
    dateLabel: 'Data de envio',
    expectedReturnDateLabel: 'Previsão de retorno',
  },
  RETURNED: {
    menuLabel: 'Registrar retorno',
    dialogTitle: 'Registrar retorno do laboratório',
    description: 'O trabalho voltou do laboratório e está na clínica.',
    dateLabel: 'Data de retorno',
  },
  IN_ADJUSTMENT: {
    menuLabel: 'Enviar para ajuste',
    dialogTitle: 'Enviar para ajuste',
    description: 'O trabalho volta para o laboratório. Informe a nova previsão de retorno.',
    dateLabel: 'Nova previsão de retorno',
  },
  DELIVERED: {
    menuLabel: 'Registrar entrega ao paciente',
    dialogTitle: 'Registrar entrega ao paciente',
    description: 'O trabalho foi instalado/entregue ao paciente e o envio será finalizado.',
    dateLabel: 'Data de entrega',
  },
  CANCELED: {
    menuLabel: 'Cancelar envio',
    dialogTitle: 'Cancelar envio',
    description: 'O envio será marcado como cancelado e não poderá mais ser alterado.',
    dateLabel: null,
  },
};

const PENDING_STATUSES = [LAB_ORDER_STATUS.SENT, LAB_ORDER_STATUS.IN_ADJUSTMENT];

// List dates come from the API as LocalDate (ISO); DD/MM/YYYY is accepted too.
export const parseApiDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed : null;
};

export const formatDisplayDate = (value) => {
  const parsed = parseApiDate(value);
  return parsed ? parsed.format('DD/MM/YYYY') : '-';
};

export const isLabOrderLate = (labOrder) =>
  PENDING_STATUSES.includes(labOrder.status) &&
  Boolean(parseApiDate(labOrder.expectedReturnDate)?.isBefore(dayjs(), 'day'));

export const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

// The backend may return an object on validation errors; rendering an object as a child
// breaks React, so we coerce to text before showing it.
export const toMessageText = (data, fallback = 'Não foi possível concluir a operação.') =>
  typeof data === 'string' && data ? data : fallback;
